import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  api,
  type CashAccount,
  type CashAdjustment,
  type CashDay,
  type CashMovement,
  type CashPositions,
  type CashStatement,
  type CashTransfer,
} from '../../api.ts';
import { changed, FormModal, FormSection } from '../../components/FormModal.tsx';
import { ListToolbar } from '../../components/ListFilters.tsx';
import { Tabs } from '../../components/Tabs.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { NetsuiteButton } from '../../components/NetsuiteReferences.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  NumCell,
  PageHeader,
  Panel,
  Select,
  Table,
  Textarea,
} from '../../components/ui.tsx';
import { normalizeDecimal } from '../fpa/expense-form.ts';
import { CommentAction } from '../partners/common.tsx';
import { useFormat } from '../purchasing/common.tsx';
import { CardFilterNotice, useCardFilter } from './card-filter.tsx';
import { TreasuryBudget, TreasuryReports, TreasuryScenarios } from './TreasuryAnalysis.tsx';
import { TreasuryFlow } from './TreasuryFlow.tsx';
import { TreasuryMonthly, TreasuryOverview } from './TreasuryOverview.tsx';
import { TreasuryForecasts } from './TreasuryForecasts.tsx';
import { ImportStatementForm } from './TreasuryImport.tsx';
import { AccountHolds, TreasuryReceipts } from './TreasuryReceipts.tsx';
import { TreasuryReconciliation } from './TreasuryReconciliation.tsx';

type Tone = 'neutral' | 'ok' | 'off' | 'info' | 'accent';
const DAY_TONE: Record<CashDay['status'], Tone> = {
  before_opening: 'neutral',
  pending: 'accent',
  reconciled: 'ok',
  divergent: 'off',
};
const KINDS = ['payment', 'receipt', 'transfer', 'fee', 'interest', 'tax', 'fx', 'other'] as const;
const QUERY_KEY = ['finance', 'treasury'];
const PROPOSED_FILTER = ['proposed_adjustments'] as const;

const iso = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => iso(new Date(Date.now() - n * 86_400_000));
const dec = (raw: string) => normalizeDecimal(raw);

function useCashAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) =>
      api.post<Record<string, unknown>>(`/finance/treasury${path}`, body ?? {}),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

const TABS = [
  'overview',
  'flow',
  'monthly',
  'forecasts',
  'receipts',
  'balances',
  'reconciliation',
  'budget',
  'scenarios',
  'reports',
] as const;

const accountLabel = (a: CashAccount) =>
  `${a.legalEntityCode ?? '—'} · ${a.nickname} · ${a.masked} · ${a.currency}`;

/**
 * Tesouraria (ADR 0084): Fluxo diário (projeção, Entrega 3) e Saldos bancários (saldo de cada
 * conta dia a dia, extratos digitados, ajustes e transferências, Entrega 2a).
 */
export function TreasuryPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<
    | 'overview'
    | 'flow'
    | 'monthly'
    | 'forecasts'
    | 'receipts'
    | 'balances'
    | 'reconciliation'
    | 'budget'
    | 'scenarios'
    | 'reports'
  >(() => {
    const wanted = new URLSearchParams(window.location.search).get('tab');
    return TABS.includes(wanted as (typeof TABS)[number])
      ? (wanted as (typeof TABS)[number])
      : 'overview';
  });
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('nav.treasury')} description={t('finance.cash.about')} />
      <div className="print:hidden">
        <Tabs
          items={[
            { id: 'overview', label: t('finance.overview.tab') },
            { id: 'flow', label: t('finance.flow.tab') },
            { id: 'monthly', label: t('finance.monthly.tab') },
            { id: 'forecasts', label: t('finance.forecasts.tab') },
            { id: 'receipts', label: t('finance.receipts.tab') },
            { id: 'balances', label: t('finance.cash.tab') },
            { id: 'reconciliation', label: t('finance.recon.tab') },
            { id: 'budget', label: t('finance.budget.tab') },
            { id: 'scenarios', label: t('finance.scenarios.tab') },
            { id: 'reports', label: t('finance.reports.tab') },
          ]}
          value={tab}
          onChange={setTab}
          label={t('nav.treasury')}
          idPrefix="treasury"
        />
      </div>
      <div role="tabpanel" id={`treasury-panel-${tab}`}>
        {tab === 'overview' ? (
          <TreasuryOverview />
        ) : tab === 'monthly' ? (
          <TreasuryMonthly />
        ) : tab === 'budget' ? (
          <TreasuryBudget />
        ) : tab === 'scenarios' ? (
          <TreasuryScenarios />
        ) : tab === 'reports' ? (
          <TreasuryReports />
        ) : tab === 'flow' ? (
          <TreasuryFlow />
        ) : tab === 'forecasts' ? (
          <TreasuryForecasts />
        ) : tab === 'receipts' ? (
          <TreasuryReceipts />
        ) : tab === 'balances' ? (
          <CashBalances />
        ) : (
          <TreasuryReconciliation />
        )}
      </div>
    </div>
  );
}

/** Saldo de cada conta dia a dia, extratos, ajustes e transferências (Entrega 2a). */
function CashBalances() {
  const { t } = useTranslation();
  const [proposedOnly, clearProposed] = useCardFilter(PROPOSED_FILTER);
  const [accountKey, setAccountKey] = useState('');
  const [from, setFrom] = useState(() => daysAgo(30));
  const [to, setTo] = useState(() => iso(new Date()));
  const [form, setForm] = useState<
    | null
    | { kind: 'balance' }
    | { kind: 'statement' }
    | { kind: 'import' }
    | { kind: 'transfer' }
    | { kind: 'adjustment'; day?: CashDay }
    | { kind: 'correct'; day: CashDay }
  >(null);
  const accounts = useQuery({
    queryKey: [...QUERY_KEY, 'accounts'],
    queryFn: () => api.get<{ accounts: CashAccount[] }>('/finance/treasury/accounts'),
  });
  if (accounts.isPending) return <Loading />;
  if (accounts.isError) return <ErrorBox error={accounts.error} />;
  const list = accounts.data.accounts;
  if (proposedOnly) {
    return (
      <div className="flex flex-col gap-6">
        <CardFilterNotice
          label={t('finance.homeCards.proposedAdjustments.title')}
          onClear={clearProposed}
        />
        <Adjustments accounts={list} status="proposed" />
      </div>
    );
  }
  const selected = list.find((a) => a.accountKey === accountKey) ?? list[0] ?? null;
  const canManageAny = list.some((a) => a.canManage);
  return (
    <div className="flex flex-col gap-6">
      {list.length === 0 ? (
        <Panel>
          <p className="text-sm text-muted">{t('finance.cash.noAccounts')}</p>
        </Panel>
      ) : (
        <>
          <ListToolbar
            action={
              selected?.canManage && (
                <div className="flex flex-wrap gap-2">
                  {canManageAny && list.length > 1 && (
                    <Button variant="secondary" onClick={() => setForm({ kind: 'transfer' })}>
                      {t('finance.cash.newTransfer')}
                    </Button>
                  )}
                  <Button variant="secondary" onClick={() => setForm({ kind: 'balance' })}>
                    {t('finance.cash.reportBalance')}
                  </Button>
                  <Button variant="secondary" onClick={() => setForm({ kind: 'adjustment' })}>
                    {t('finance.cash.proposeAdjustment')}
                  </Button>
                  <Button variant="secondary" onClick={() => setForm({ kind: 'statement' })}>
                    {t('finance.cash.enterStatement')}
                  </Button>
                  <Button onClick={() => setForm({ kind: 'import' })}>
                    {t('finance.import.title')}
                  </Button>
                </div>
              )
            }
          >
            <Select
              id="cash-account"
              aria-label={t('finance.cash.fields.account')}
              value={selected?.accountKey ?? ''}
              onChange={(e) => setAccountKey(e.target.value)}
            >
              {list.map((a) => (
                <option key={a.accountKey} value={a.accountKey}>
                  {accountLabel(a)}
                </option>
              ))}
            </Select>
            <Input
              id="cash-from"
              type="date"
              aria-label={t('finance.cash.fields.from')}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <Input
              id="cash-to"
              type="date"
              aria-label={t('finance.cash.fields.to')}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </ListToolbar>
          {selected && (
            <AccountCash
              account={selected}
              from={from}
              to={to}
              onAdjust={(day) => setForm({ kind: 'adjustment', day })}
              onCorrect={(day) => setForm({ kind: 'correct', day })}
            />
          )}
          {selected && <AccountHolds account={selected} />}
          <Adjustments accounts={list} />
          <Transfers accounts={list} from={from} to={to} />
        </>
      )}
      {selected && form?.kind === 'balance' && (
        <BalanceForm account={selected} onDone={() => setForm(null)} />
      )}
      {selected && form?.kind === 'correct' && (
        <CorrectBalanceForm account={selected} day={form.day} onDone={() => setForm(null)} />
      )}
      {selected && form?.kind === 'statement' && (
        <StatementForm account={selected} onDone={() => setForm(null)} />
      )}
      {selected && form?.kind === 'import' && (
        <ImportStatementForm account={selected} onDone={() => setForm(null)} />
      )}
      {selected && form?.kind === 'adjustment' && (
        <AdjustmentForm account={selected} day={form.day} onDone={() => setForm(null)} />
      )}
      {form?.kind === 'transfer' && <TransferForm accounts={list} onDone={() => setForm(null)} />}
    </div>
  );
}

function AccountCash({
  account,
  from,
  to,
  onAdjust,
  onCorrect,
}: {
  account: CashAccount;
  from: string;
  to: string;
  onAdjust: (day: CashDay) => void;
  onCorrect: (day: CashDay) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const key = account.accountKey;
  const positions = useQuery({
    queryKey: [...QUERY_KEY, 'positions', key, from, to],
    queryFn: () =>
      api.get<CashPositions>(`/finance/treasury/accounts/${key}/positions?from=${from}&to=${to}`),
  });
  const money = (v: string | null) => fmt.money(v, account.currency);
  return (
    <>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-muted">{t('finance.cash.fields.openingDate')}</dt>
          <dd>
            {account.openingDate ? fmt.date(account.openingDate) : t('finance.cash.noOpening')}
          </dd>
        </div>
        <div>
          <dt className="text-muted">{t('finance.cash.fields.lastReported')}</dt>
          <dd>
            {account.lastReported
              ? `${money(account.lastReported.amount)} · ${fmt.date(account.lastReported.date)}`
              : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-muted">{t('finance.cash.fields.lastCoveredDay')}</dt>
          <dd>{account.lastCoveredDay ? fmt.date(account.lastCoveredDay) : '—'}</dd>
        </div>
        <div>
          <dt className="text-muted">{t('finance.cash.available')}</dt>
          <dd>
            {account.available === null ? '—' : money(account.available)}
            {account.held !== '0.0000' && (
              <span className="text-muted">
                {' '}
                · {t('finance.cash.heldAmount', { amount: money(account.held) })}
              </span>
            )}
            {account.kind === 'platform' && (
              <div className="text-xs text-muted">{t('finance.cash.platformApart')}</div>
            )}
          </dd>
        </div>
      </dl>
      {positions.isPending ? (
        <Loading />
      ) : positions.isError ? (
        <ErrorBox error={positions.error} />
      ) : (
        <Table
          head={[
            t('finance.cash.fields.date'),
            { label: t('finance.cash.fields.opening'), align: 'right' },
            { label: t('finance.cash.fields.credits'), align: 'right' },
            { label: t('finance.cash.fields.debits'), align: 'right' },
            { label: t('finance.cash.fields.calculated'), align: 'right' },
            { label: t('finance.cash.fields.reported'), align: 'right' },
            { label: t('finance.cash.fields.difference'), align: 'right' },
            t('finance.cash.fields.situation'),
            '',
          ]}
        >
          {[...positions.data.days].reverse().map((d) => (
            <tr key={d.date}>
              <td className="whitespace-nowrap">
                {fmt.date(d.date)}
                <div className="text-xs text-muted">
                  {d.covered
                    ? t('finance.cash.coveredByStatement')
                    : t('finance.cash.bySettlements')}
                </div>
              </td>
              <NumCell>{money(d.opening)}</NumCell>
              <NumCell>{money(d.credits)}</NumCell>
              <NumCell>{money(d.debits)}</NumCell>
              <NumCell>{money(d.calculated)}</NumCell>
              <NumCell>{money(d.reported)}</NumCell>
              <NumCell>
                {d.difference && d.difference !== '0.0000' ? money(d.difference) : '—'}
              </NumCell>
              <td>
                <Badge tone={DAY_TONE[d.status]}>{t(`finance.cash.statuses.${d.status}`)}</Badge>
                {d.adjusted && (
                  <div className="text-xs text-muted">{t('finance.cash.adjustedDay')}</div>
                )}
              </td>
              <td className="text-right">
                {account.canManage && (
                  <div className="flex justify-end gap-2">
                    {d.status === 'divergent' && (
                      <Button variant="secondary" onClick={() => onAdjust(d)}>
                        {t('finance.cash.adjust')}
                      </Button>
                    )}
                    {d.reportedId && (
                      <Button variant="secondary" onClick={() => onCorrect(d)}>
                        {t('finance.cash.correctBalance')}
                      </Button>
                    )}
                    {d.reportedId && (
                      <MasterDeleteButton
                        table="treasury.reported_balance"
                        id={d.reportedId}
                        compact
                      />
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
      <Movements account={account} from={from} to={to} />
      <Statements account={account} />
    </>
  );
}

function Movements({ account, from, to }: { account: CashAccount; from: string; to: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useCashAction();
  const key = account.accountKey;
  const data = useQuery({
    queryKey: [...QUERY_KEY, 'movements', key, from, to],
    queryFn: () =>
      api.get<{ movements: CashMovement[] }>(
        `/finance/treasury/accounts/${key}/movements?from=${from}&to=${to}`,
      ),
  });
  return (
    <Panel title={t('finance.cash.movementsTitle')}>
      {data.isPending ? (
        <Loading />
      ) : data.isError ? (
        <ErrorBox error={data.error} />
      ) : (
        <Table
          head={[
            t('finance.cash.fields.date'),
            t('finance.cash.fields.description'),
            t('finance.cash.fields.kind'),
            { label: t('finance.cash.fields.amount'), align: 'right' },
            '',
          ]}
        >
          {data.data.movements.length === 0 && (
            <EmptyRow colSpan={5}>{t('finance.cash.noMovements')}</EmptyRow>
          )}
          {data.data.movements.map((m) => (
            <tr key={m.id}>
              <td className="whitespace-nowrap">{fmt.date(m.valueDate)}</td>
              <td>
                {m.description}
                {m.bankReference && (
                  <div className="font-mono text-xs text-muted">{m.bankReference}</div>
                )}
                {m.note && <div className="text-xs text-muted">{m.note}</div>}
                {m.reversed && <Badge tone="neutral">{t('finance.cash.reversed')}</Badge>}
              </td>
              <td className="text-sm">{t(`finance.cash.kinds.${m.kind}`)}</td>
              <NumCell>
                {m.direction === 'out' ? '−' : '+'} {fmt.money(m.amount, m.currency)}
              </NumCell>
              <td className="text-right">
                <div className="flex justify-end gap-2">
                  {m.canReverse && (
                    <CommentAction
                      label={t('finance.cash.reverse')}
                      fieldLabel={t('finance.cash.fields.reason')}
                      pending={action.isPending}
                      error={action.error}
                      onConfirm={(note) =>
                        action.mutate({ path: `/movements/${m.id}/reverse`, body: { note } })
                      }
                    />
                  )}
                  <NetsuiteButton entityType="treasury_movement" entityId={m.id} />
                  <MasterDeleteButton table="treasury.bank_movement" id={m.id} compact />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

function Statements({ account }: { account: CashAccount }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useCashAction();
  const key = account.accountKey;
  const data = useQuery({
    queryKey: [...QUERY_KEY, 'statements', key],
    queryFn: () =>
      api.get<{ statements: CashStatement[] }>(`/finance/treasury/accounts/${key}/statements`),
  });
  return (
    <Panel title={t('finance.cash.statementsTitle')}>
      {data.isPending ? (
        <Loading />
      ) : data.isError ? (
        <ErrorBox error={data.error} />
      ) : (
        <Table
          head={[
            t('finance.cash.fields.period'),
            t('finance.cash.fields.lines'),
            t('finance.cash.fields.recordedBy'),
            t('finance.cash.fields.situation'),
            '',
          ]}
        >
          {data.data.statements.length === 0 && (
            <EmptyRow colSpan={5}>{t('finance.cash.noStatements')}</EmptyRow>
          )}
          {data.data.statements.map((s) => (
            <tr key={s.id}>
              <td className="whitespace-nowrap">
                {fmt.date(s.periodStart)} → {fmt.date(s.periodEnd)}
              </td>
              <td className="text-sm">
                {t('finance.cash.linesSummary', {
                  recorded: s.linesRecorded,
                  repeated: s.linesRepeated,
                })}
              </td>
              <td className="text-xs">
                {s.createdByName ?? '—'} · {fmt.dateTime(s.createdAt)}
              </td>
              <td>
                <Badge tone={s.status === 'recorded' ? 'ok' : 'neutral'}>
                  {t(`finance.cash.statementStatuses.${s.status}`)}
                </Badge>
                {s.discardReason && <div className="text-xs text-muted">{s.discardReason}</div>}
              </td>
              <td className="text-right">
                <div className="flex justify-end gap-2">
                  {s.canDiscard && (
                    <CommentAction
                      label={t('finance.cash.discard')}
                      fieldLabel={t('finance.cash.fields.reason')}
                      variant="danger"
                      pending={action.isPending}
                      error={action.error}
                      onConfirm={(reason) =>
                        action.mutate({ path: `/statements/${s.id}/discard`, body: { reason } })
                      }
                    />
                  )}
                  <MasterDeleteButton table="treasury.statement_import" id={s.id} compact />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

function Adjustments({ accounts, status }: { accounts: CashAccount[]; status?: 'proposed' }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useCashAction();
  const data = useQuery({
    queryKey: [...QUERY_KEY, 'adjustments', status],
    queryFn: () =>
      api.get<{ adjustments: CashAdjustment[] }>(
        `/finance/treasury/adjustments${status ? `?status=${status}` : ''}`,
      ),
  });
  const nameOf = new Map(accounts.map((a) => [a.accountKey, accountLabel(a)]));
  return (
    <Panel title={t('finance.cash.adjustmentsTitle')}>
      {data.isPending ? (
        <Loading />
      ) : data.isError ? (
        <ErrorBox error={data.error} />
      ) : (
        <Table
          head={[
            t('finance.cash.fields.account'),
            t('finance.cash.fields.date'),
            { label: t('finance.cash.fields.amount'), align: 'right' },
            t('finance.cash.fields.reason'),
            t('finance.cash.fields.situation'),
            '',
          ]}
        >
          {data.data.adjustments.length === 0 && (
            <EmptyRow colSpan={6}>{t('finance.cash.noAdjustments')}</EmptyRow>
          )}
          {data.data.adjustments.map((a) => (
            <tr key={a.id}>
              <td className="text-xs">{nameOf.get(a.accountKey) ?? '—'}</td>
              <td className="whitespace-nowrap">{fmt.date(a.adjustmentDate)}</td>
              <NumCell>
                {a.direction === 'out' ? '−' : '+'} {fmt.money(a.amount, a.currency)}
              </NumCell>
              <td className="text-sm">
                {a.reason}
                <div className="text-xs text-muted">
                  {t('finance.cash.proposedBy', { who: a.createdByName ?? '—' })}
                </div>
                {a.decisionComment && <div className="text-xs text-muted">{a.decisionComment}</div>}
              </td>
              <td>
                <Badge
                  tone={
                    a.status === 'confirmed' ? 'ok' : a.status === 'proposed' ? 'accent' : 'off'
                  }
                >
                  {t(`finance.cash.adjustmentStatuses.${a.status}`)}
                </Badge>
                {a.actions.decideBlockedBySeparation && (
                  <div className="text-xs text-muted">{t('finance.cash.separation')}</div>
                )}
              </td>
              <td className="text-right">
                <div className="flex justify-end gap-2">
                  {a.actions.decide && (
                    <>
                      <CommentAction
                        label={t('finance.cash.confirm')}
                        fieldLabel={t('finance.cash.fields.comment')}
                        variant="primary"
                        pending={action.isPending}
                        error={action.error}
                        onConfirm={(comment) =>
                          action.mutate({ path: `/adjustments/${a.id}/confirm`, body: { comment } })
                        }
                      />
                      <CommentAction
                        label={t('finance.cash.reject')}
                        fieldLabel={t('finance.cash.fields.reason')}
                        variant="danger"
                        pending={action.isPending}
                        error={action.error}
                        onConfirm={(comment) =>
                          action.mutate({ path: `/adjustments/${a.id}/reject`, body: { comment } })
                        }
                      />
                    </>
                  )}
                  <NetsuiteButton entityType="treasury_adjustment" entityId={a.id} />
                  <MasterDeleteButton table="treasury.balance_adjustment" id={a.id} compact />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

function Transfers({ accounts, from, to }: { accounts: CashAccount[]; from: string; to: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useCashAction();
  const data = useQuery({
    queryKey: [...QUERY_KEY, 'transfers', from, to],
    queryFn: () =>
      api.get<{ transfers: CashTransfer[] }>(`/finance/treasury/transfers?from=${from}&to=${to}`),
  });
  const nameOf = new Map(accounts.map((a) => [a.accountKey, accountLabel(a)]));
  return (
    <Panel title={t('finance.cash.transfersTitle')}>
      {data.isPending ? (
        <Loading />
      ) : data.isError ? (
        <ErrorBox error={data.error} />
      ) : (
        <Table
          head={[
            t('finance.cash.fields.date'),
            t('finance.cash.fields.fromAccount'),
            t('finance.cash.fields.toAccount'),
            { label: t('finance.cash.fields.amount'), align: 'right' },
            t('finance.cash.fields.situation'),
            '',
          ]}
        >
          {data.data.transfers.length === 0 && (
            <EmptyRow colSpan={6}>{t('finance.cash.noTransfers')}</EmptyRow>
          )}
          {data.data.transfers.map((tr) => (
            <tr key={tr.id}>
              <td className="whitespace-nowrap">
                {fmt.date(tr.outDate)}
                {tr.inDate !== tr.outDate && (
                  <div className="text-xs text-muted">→ {fmt.date(tr.inDate)}</div>
                )}
              </td>
              <td className="text-xs">{nameOf.get(tr.fromAccountKey) ?? '—'}</td>
              <td className="text-xs">
                {nameOf.get(tr.toAccountKey) ?? '—'}
                {tr.isIntercompany && <Badge tone="info">{t('finance.cash.intercompany')}</Badge>}
              </td>
              <NumCell>
                {fmt.money(tr.fromAmount, tr.fromCurrency)}
                {tr.fromCurrency !== tr.toCurrency && (
                  <div className="text-xs text-muted">{fmt.money(tr.toAmount, tr.toCurrency)}</div>
                )}
              </NumCell>
              <td>
                <Badge tone={tr.status === 'recorded' ? 'ok' : 'neutral'}>
                  {t(`finance.cash.transferStatuses.${tr.status}`)}
                </Badge>
                <div className="text-xs text-muted">{tr.cancelReason ?? tr.description}</div>
              </td>
              <td className="text-right">
                <div className="flex justify-end gap-2">
                  {tr.canCancel && (
                    <CommentAction
                      label={t('finance.cash.cancelTransfer')}
                      fieldLabel={t('finance.cash.fields.reason')}
                      variant="danger"
                      pending={action.isPending}
                      error={action.error}
                      onConfirm={(reason) =>
                        action.mutate({ path: `/transfers/${tr.id}/cancel`, body: { reason } })
                      }
                    />
                  )}
                  <NetsuiteButton entityType="treasury_transfer" entityId={tr.id} />
                  <MasterDeleteButton table="treasury.transfer" id={tr.id} compact />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}

function BalanceForm({ account, onDone }: { account: CashAccount; onDone: () => void }) {
  const { t } = useTranslation();
  const action = useCashAction();
  const [start] = useState(() => ({
    balanceDate: daysAgo(1),
    amount: '',
    isOpening: !account.openingDate,
  }));
  const [f, setF] = useState(start);
  return (
    <FormModal
      open
      size="md"
      title={t('finance.cash.reportBalance')}
      description={t('finance.cash.balanceHint', { account: accountLabel(account) })}
      dirty={changed(start, f)}
      saving={action.isPending}
      error={action.error}
      onSave={() =>
        action.mutate(
          {
            path: '/balances',
            body: { accountKey: account.accountKey, ...f, amount: dec(f.amount) },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('finance.cash.fields.date')} required>
          <Input
            id="cash-balance-date"
            type="date"
            required
            value={f.balanceDate}
            onChange={(e) => setF((s) => ({ ...s, balanceDate: e.target.value }))}
          />
        </Field>
        <Field
          label={t('finance.cash.fields.reportedAmount', { currency: account.currency })}
          hint={t('finance.cash.amountHint')}
          required
        >
          <Input
            id="cash-balance-amount"
            inputMode="decimal"
            required
            value={f.amount}
            onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))}
          />
        </Field>
        {!account.openingDate && (
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={f.isOpening}
              onChange={(e) => setF((s) => ({ ...s, isOpening: e.target.checked }))}
            />
            {t('finance.cash.isOpening')}
          </label>
        )}
      </FormSection>
    </FormModal>
  );
}

function CorrectBalanceForm({
  account,
  day,
  onDone,
}: {
  account: CashAccount;
  day: CashDay;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useCashAction();
  const [start] = useState(() => ({ amount: '', reason: '' }));
  const [f, setF] = useState(start);
  return (
    <FormModal
      open
      size="md"
      title={t('finance.cash.correctBalance')}
      description={t('finance.cash.correctHint', {
        date: fmt.date(day.date),
        amount: fmt.money(day.reported, account.currency),
      })}
      dirty={changed(start, f)}
      saving={action.isPending}
      error={action.error}
      onSave={() =>
        action.mutate(
          {
            path: `/balances/${day.reportedId}/correct`,
            body: { amount: dec(f.amount), reason: f.reason },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        <Field
          label={t('finance.cash.fields.reportedAmount', { currency: account.currency })}
          hint={t('finance.cash.amountHint')}
          required
        >
          <Input
            id="cash-correct-amount"
            inputMode="decimal"
            required
            value={f.amount}
            onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))}
          />
        </Field>
        <Field label={t('finance.cash.fields.reason')} className="sm:col-span-2" required>
          <Textarea
            id="cash-correct-reason"
            required
            minLength={3}
            value={f.reason}
            onChange={(e) => setF((s) => ({ ...s, reason: e.target.value }))}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

type LineForm = {
  valueDate: string;
  direction: 'in' | 'out';
  amount: string;
  description: string;
  bankReference: string;
  kind: (typeof KINDS)[number];
};

function StatementForm({ account, onDone }: { account: CashAccount; onDone: () => void }) {
  const { t } = useTranslation();
  const action = useCashAction();
  const newLine = (valueDate: string): LineForm => ({
    valueDate,
    direction: 'out',
    amount: '',
    description: '',
    bankReference: '',
    kind: 'other',
  });
  const [start] = useState(() => ({
    periodStart: daysAgo(1),
    periodEnd: daysAgo(1),
    closingBalance: '',
    lines: [newLine(daysAgo(1))],
  }));
  const [f, setF] = useState(start);
  const setLine = (i: number, patch: Partial<LineForm>) =>
    setF((s) => ({ ...s, lines: s.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  return (
    <FormModal
      open
      size="xl"
      title={t('finance.cash.enterStatement')}
      description={t('finance.cash.statementHint', { account: accountLabel(account) })}
      dirty={changed(start, f)}
      saving={action.isPending}
      error={action.error}
      onSave={() =>
        action.mutate(
          {
            path: '/statements',
            body: {
              accountKey: account.accountKey,
              periodStart: f.periodStart,
              periodEnd: f.periodEnd,
              closingBalance: f.closingBalance.trim() ? dec(f.closingBalance) : null,
              lines: f.lines
                .filter((l) => l.amount.trim() || l.description.trim())
                .map((l) => ({ ...l, amount: dec(l.amount), bankReference: l.bankReference })),
            },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('finance.cash.fields.periodStart')} required>
          <Input
            id="cash-st-start"
            type="date"
            required
            value={f.periodStart}
            onChange={(e) => setF((s) => ({ ...s, periodStart: e.target.value }))}
          />
        </Field>
        <Field label={t('finance.cash.fields.periodEnd')} required>
          <Input
            id="cash-st-end"
            type="date"
            required
            value={f.periodEnd}
            onChange={(e) => setF((s) => ({ ...s, periodEnd: e.target.value }))}
          />
        </Field>
        <Field
          label={t('finance.cash.fields.closingBalance', { currency: account.currency })}
          hint={t('finance.cash.closingHint')}
        >
          <Input
            id="cash-st-closing"
            inputMode="decimal"
            value={f.closingBalance}
            onChange={(e) => setF((s) => ({ ...s, closingBalance: e.target.value }))}
          />
        </Field>
      </FormSection>
      <div className="mt-4 flex flex-col gap-3">
        <p className="text-sm font-medium">{t('finance.cash.linesTitle')}</p>
        {f.lines.map((l, i) => (
          <div
            key={i}
            className="grid grid-cols-1 gap-2 rounded-md border border-rule p-3 sm:grid-cols-6"
          >
            <Input
              id={`cash-line-date-${i}`}
              type="date"
              aria-label={t('finance.cash.fields.date')}
              value={l.valueDate}
              onChange={(e) => setLine(i, { valueDate: e.target.value })}
            />
            <Select
              id={`cash-line-dir-${i}`}
              aria-label={t('finance.cash.fields.direction')}
              value={l.direction}
              onChange={(e) => setLine(i, { direction: e.target.value as 'in' | 'out' })}
            >
              <option value="out">{t('finance.cash.directions.out')}</option>
              <option value="in">{t('finance.cash.directions.in')}</option>
            </Select>
            <Input
              id={`cash-line-amount-${i}`}
              inputMode="decimal"
              placeholder={t('finance.cash.fields.amount')}
              aria-label={t('finance.cash.fields.amount')}
              value={l.amount}
              onChange={(e) => setLine(i, { amount: e.target.value })}
            />
            <Input
              id={`cash-line-desc-${i}`}
              className="sm:col-span-2"
              placeholder={t('finance.cash.fields.description')}
              aria-label={t('finance.cash.fields.description')}
              value={l.description}
              onChange={(e) => setLine(i, { description: e.target.value })}
            />
            <Select
              id={`cash-line-kind-${i}`}
              aria-label={t('finance.cash.fields.kind')}
              value={l.kind}
              onChange={(e) => setLine(i, { kind: e.target.value as LineForm['kind'] })}
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`finance.cash.kinds.${k}`)}
                </option>
              ))}
            </Select>
            <Input
              id={`cash-line-ref-${i}`}
              className="sm:col-span-5"
              placeholder={t('finance.cash.fields.bankReference')}
              aria-label={t('finance.cash.fields.bankReference')}
              value={l.bankReference}
              onChange={(e) => setLine(i, { bankReference: e.target.value })}
            />
            <Button
              variant="secondary"
              disabled={f.lines.length === 1}
              onClick={() => setF((s) => ({ ...s, lines: s.lines.filter((_, j) => j !== i) }))}
            >
              {t('finance.cash.removeLine')}
            </Button>
          </div>
        ))}
        <div>
          <Button
            variant="secondary"
            onClick={() =>
              setF((s) => ({
                ...s,
                lines: [...s.lines, newLine(s.lines.at(-1)?.valueDate ?? s.periodEnd)],
              }))
            }
          >
            {t('finance.cash.addLine')}
          </Button>
        </div>
      </div>
    </FormModal>
  );
}

function AdjustmentForm({
  account,
  day,
  onDone,
}: {
  account: CashAccount;
  day?: CashDay;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const action = useCashAction();
  // A diferença é informado − calculado: falta no sistema uma entrada (positiva) ou uma saída.
  const diff = day?.difference ?? null;
  const [start] = useState(() => ({
    adjustmentDate: day?.date ?? daysAgo(1),
    direction: (diff && diff.startsWith('-') ? 'out' : 'in') as 'in' | 'out',
    amount: diff ? diff.replace('-', '') : '',
    reason: '',
  }));
  const [f, setF] = useState(start);
  return (
    <FormModal
      open
      size="md"
      title={t('finance.cash.proposeAdjustment')}
      description={t('finance.cash.adjustmentHint', { account: accountLabel(account) })}
      dirty={changed(start, f)}
      saving={action.isPending}
      error={action.error}
      saveLabel={t('finance.cash.sendForConfirmation')}
      onSave={() =>
        action.mutate(
          {
            path: '/adjustments',
            body: { accountKey: account.accountKey, ...f, amount: dec(f.amount) },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('finance.cash.fields.date')} required>
          <Input
            id="cash-adj-date"
            type="date"
            required
            value={f.adjustmentDate}
            onChange={(e) => setF((s) => ({ ...s, adjustmentDate: e.target.value }))}
          />
        </Field>
        <Field label={t('finance.cash.fields.direction')} required>
          <Select
            id="cash-adj-direction"
            value={f.direction}
            onChange={(e) => setF((s) => ({ ...s, direction: e.target.value as 'in' | 'out' }))}
          >
            <option value="out">{t('finance.cash.directions.out')}</option>
            <option value="in">{t('finance.cash.directions.in')}</option>
          </Select>
        </Field>
        <Field
          label={t('finance.cash.fields.amountIn', { currency: account.currency })}
          hint={t('finance.cash.amountHint')}
          required
        >
          <Input
            id="cash-adj-amount"
            inputMode="decimal"
            required
            value={f.amount}
            onChange={(e) => setF((s) => ({ ...s, amount: e.target.value }))}
          />
        </Field>
        <Field label={t('finance.cash.fields.reason')} className="sm:col-span-2" required>
          <Textarea
            id="cash-adj-reason"
            required
            minLength={3}
            value={f.reason}
            onChange={(e) => setF((s) => ({ ...s, reason: e.target.value }))}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

function TransferForm({ accounts, onDone }: { accounts: CashAccount[]; onDone: () => void }) {
  const { t } = useTranslation();
  const action = useCashAction();
  const usable = accounts.filter((a) => a.canManage);
  const [start] = useState(() => ({
    fromAccountKey: usable[0]?.accountKey ?? '',
    toAccountKey: usable[1]?.accountKey ?? '',
    fromAmount: '',
    toAmount: '',
    exchangeRate: '',
    feeAmount: '',
    outDate: iso(new Date()),
    inDate: '',
    description: '',
  }));
  const [f, setF] = useState(start);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  const fromAcc = usable.find((a) => a.accountKey === f.fromAccountKey);
  const toAcc = usable.find((a) => a.accountKey === f.toAccountKey);
  const otherCurrency = !!fromAcc && !!toAcc && fromAcc.currency !== toAcc.currency;
  const choose = (k: 'fromAccountKey' | 'toAccountKey') => (
    <Select id={`cash-tr-${k}`} required value={f[k]} onChange={set(k)}>
      {usable.map((a) => (
        <option key={a.accountKey} value={a.accountKey}>
          {accountLabel(a)}
        </option>
      ))}
    </Select>
  );
  return (
    <FormModal
      open
      size="lg"
      title={t('finance.cash.newTransfer')}
      description={t('finance.cash.transferHint')}
      dirty={changed(start, f)}
      saving={action.isPending}
      error={action.error}
      onSave={() =>
        action.mutate(
          {
            path: '/transfers',
            body: {
              fromAccountKey: f.fromAccountKey,
              toAccountKey: f.toAccountKey,
              fromAmount: dec(f.fromAmount),
              toAmount: otherCurrency ? dec(f.toAmount) : null,
              exchangeRate: otherCurrency ? dec(f.exchangeRate) : null,
              feeAmount: f.feeAmount.trim() ? dec(f.feeAmount) : '0',
              outDate: f.outDate,
              inDate: f.inDate || null,
              description: f.description,
            },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('finance.cash.fields.fromAccount')} required>
          {choose('fromAccountKey')}
        </Field>
        <Field label={t('finance.cash.fields.toAccount')} required>
          {choose('toAccountKey')}
        </Field>
        <Field
          label={t('finance.cash.fields.amountIn', { currency: fromAcc?.currency ?? '' })}
          hint={t('finance.cash.amountHint')}
          required
        >
          <Input
            id="cash-tr-amount"
            inputMode="decimal"
            required
            value={f.fromAmount}
            onChange={set('fromAmount')}
          />
        </Field>
        <Field label={t('finance.cash.fields.fee', { currency: fromAcc?.currency ?? '' })}>
          <Input
            id="cash-tr-fee"
            inputMode="decimal"
            value={f.feeAmount}
            onChange={set('feeAmount')}
          />
        </Field>
        {otherCurrency && (
          <>
            <Field
              label={t('finance.cash.fields.amountIn', { currency: toAcc?.currency ?? '' })}
              required
            >
              <Input
                id="cash-tr-to-amount"
                inputMode="decimal"
                required
                value={f.toAmount}
                onChange={set('toAmount')}
              />
            </Field>
            <Field label={t('finance.cash.fields.exchangeRate')} required>
              <Input
                id="cash-tr-rate"
                inputMode="decimal"
                required
                value={f.exchangeRate}
                onChange={set('exchangeRate')}
              />
            </Field>
          </>
        )}
        <Field label={t('finance.cash.fields.outDate')} required>
          <Input
            id="cash-tr-out"
            type="date"
            required
            value={f.outDate}
            onChange={set('outDate')}
          />
        </Field>
        <Field label={t('finance.cash.fields.inDate')} hint={t('finance.cash.inDateHint')}>
          <Input id="cash-tr-in" type="date" value={f.inDate} onChange={set('inDate')} />
        </Field>
        <Field label={t('finance.cash.fields.description')} className="sm:col-span-2" required>
          <Input
            id="cash-tr-desc"
            required
            minLength={3}
            value={f.description}
            onChange={set('description')}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}
